import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CompensationOccupation } from '../../../models/compensationOccupation';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-compensation-occupation-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './compensation-occupation-list.component.html',
  styleUrl: './compensation-occupation-list.component.scss'
})
export class CompensationOccupationListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  compensationOccupations = signal<CompensationOccupation[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCompensationOccupations();
  }

  loadCompensationOccupations(): void {
    this.spinnerService.show();
    this.lutService.getCompensationOccupations().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortCompensationOccupations(data);
        this.compensationOccupations.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading compensation occupations:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCompensationOccupations(compensationOccupations: CompensationOccupation[]): CompensationOccupation[] {
    return [...compensationOccupations].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateCompensationOccupation(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(compensationOccupation: CompensationOccupation): void {
  }

  onEditCompensationOccupation(compensationOccupation: CompensationOccupation): void {
  }

  onDeleteCompensationOccupation(compensationOccupation: CompensationOccupation): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(compensationOccupation.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalCompensationOccupations = this.compensationOccupations();

          // 1. Optimistically remove from local state immediately
          this.compensationOccupations.update(compensationOccupations => compensationOccupations.filter(co => co.id !== compensationOccupation.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCompensationOccupation(compensationOccupation.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Compensation occupation deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting compensation occupation:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.compensationOccupations.set(originalCompensationOccupations);
              this.toast.error(`<strong>Failed to delete Compensation Occupation:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CompensationOccupation, newName: string} | {item: CompensationOccupation, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: CompensationOccupation;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.compensationOccupations.update(compensationOccupations => {
      const updated = compensationOccupations.map(co => co.id === event.item.id ? updatedItem : co);
      return this.sortCompensationOccupations(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCompensationOccupation(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Compensation occupation updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.compensationOccupations.update(compensationOccupations => {
          const updated = compensationOccupations.map(co => co.id === event.item.id ? response : co);
          return this.sortCompensationOccupations(updated);
        });
      },
      error: (error) => {
        console.error('Error updating compensation occupation:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.compensationOccupations.update(compensationOccupations =>
          compensationOccupations.map(co => co.id === event.item.id ? originalItem : co)
        );
        this.toast.error(`<strong>Failed to update Compensation Occupation:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newCompensationOccupation: CompensationOccupation = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.compensationOccupations.update(compensationOccupations => this.sortCompensationOccupations([...compensationOccupations, newCompensationOccupation]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCompensationOccupation({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Compensation occupation created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.compensationOccupations.update(compensationOccupations => {
          const updated = compensationOccupations.map(co => co.id === tempId ? response : co);
          return this.sortCompensationOccupations(updated);
        });
      },
      error: (error) => {
        console.error('Error creating compensation occupation:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.compensationOccupations.update(compensationOccupations =>
          compensationOccupations.filter(co => co.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Compensation Occupation:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
